<template>
  <div class="col">
    <div class="list-group card-item" ref="boilerItem" style="border-radius: 1.2rem;" v-touch:press="onTouchDown" v-touch:drag="onTouchDrag" v-touch:release="onTouchRelease" v-touch:hold="onTouchHold">
      <div class="list-group-item d-flex align-items-center" :style="`height: 4rem; padding: 2px 0px; background: ${cardActiveBg};`">
        <i class="fa-solid fa-temperature-three-quarters ms-3 me-2" />
        <div class="flex-fill" style="font-size: 0.8rem">
          {{ deviceLabel }}
          <br>
          <span :style="statusTextStyle">{{ statusText }}</span>
        </div>
        <div
          class="power-action-btn"
          :style="powerBtnStyle"
          @click.stop="onBoilerPowerClicked"
        >
          {{ powerActionLabel }}
        </div>
        <div :style="`min-width:1px; height: 20px; border-left: 1px solid ${cardDividerBorder};`" />
        <div class="mx-0 h-100" @click.stop="onSettingClicked">
          <i class="fa-solid fa-gear" style="font-size: 0.8rem; margin-top: 24px; width:40px; text-align: center;" />
        </div>
      </div>
    </div>
  </div>
        
  <BoilerItemModal
    v-if="boilerItemModal.visible"
    :visible="boilerItemModal.visible"
    :cancelCallback="boilerItemModal.hide"
    :roomName="props.roomName"
    :roomId="props.roomId"
    :handlePowerClick="onBoilerPowerClicked"
  />
</template>

<script setup>
import { reactive, defineProps, getCurrentInstance, computed, onBeforeUnmount, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { PowerState, MQTTTempCommand } from '@/common/constants';
import { resolveTempControllerModel } from '@/common/utils/tempControllerModel';
import BoilerItemModal from './BoilerItemModal.vue';
import useModal from '@/common/hooks/useModal';
import useMqttControl from '@/common/hooks/useMqttControl';
import useDeviceTouch from '@/common/hooks/useDeviceTouch';
import Axios, { ScheduleApi } from '@/api';

const mqttControl = reactive(useMqttControl());

const { emit } = getCurrentInstance();
const props = defineProps(['roomId', 'roomName', 'itemStyle']);
const store = useStore();
const boilerItemModal = reactive(useModal());
const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const boilerItem = ref(null);
const { 
  handlePower, 
  handleTouchDown, 
  handleTouchDrag, 
  handleTouchRelease,
  handleTouchHold
} = useDeviceTouch(props.roomId);

// 0 unknown, 1 checking, 2 online, 3 offline (ControllerStatus 와 동일)
const connectionStatus = ref(0);
const checkTimerId = ref(undefined);
const schedules = ref([]);

const BOILER_CARD = {
  heating: { bg: '#ffc107', border: '#ffb300' },
  patternHeating: { bg: '#ffc107', border: '#ffb300' },
  patternPause: { bg: '#fff8e1', border: '#ffe082', btnBg: '#fff0b3', btnBorder: '#ffda6a' },
  stopped: { bg: 'inherits', border: '#eeeeee' },
  offline: { bg: '#f5f5f5', border: '#e0e0e0', btnBg: '#eeeeee', btnBorder: '#ced4da' },
};

const zone = computed(() => {
  if (store.state.home.zones.length > 0) {
    return store.getters['home/getCurrentZone'];
  } else return null;
});
const group = computed(() => {
  return zone.value.groups.find((g) => g._id === props.roomId);
});
const devices = computed(() => {
  return group.value ? group.value.devices : undefined;
});

const boilerDevices = computed(() => {
  return devices.value.filter(
    (d) => d.type === 2,
  );
});
const primaryBoilerDevice = computed(() => {
  if (!boilerDevices.value?.length) {
    return undefined;
  }
  return (
    boilerDevices.value.find((d) => !d.isVirtual) ||
    boilerDevices.value[0]
  );
});

const mqttControllerId = computed(() => {
  const id = String(primaryBoilerDevice.value?.controllerId || '');
  if (!id) return '';
  return id.includes('-') ? id.split('-')[0] : id;
});

const checkControllers = computed(() => store.getters['home/getCheckControllers']);

const isControllerOffline = computed(() => connectionStatus.value === 3);
const isControllerChecking = computed(() => connectionStatus.value === 1);

const boilerCurrentTemp = computed(() => {
  return primaryBoilerDevice.value?.item?.currentTemp
    ? primaryBoilerDevice.value.item.currentTemp
    : 20;
});
const boilerTargetTemp = computed(() => {
  return primaryBoilerDevice.value?.item?.targetTemp
    ? primaryBoilerDevice.value.item.targetTemp
    : 18;
});

const currentTempText = computed(() => {
  return `${parseFloat(boilerCurrentTemp.value)}°C (${parseFloat(boilerTargetTemp.value)}°C)`;
});

const isAway = computed(() => {
  const device = primaryBoilerDevice.value;
  const onoff = device?.item?.onoff;
  if (onoff === undefined || onoff === null) {
    return false;
  }
  return Number(onoff) === PowerState.Off;
});

const relayOn = computed(() => primaryBoilerDevice.value?.item?.relayOn);

const boilerPattern = computed(() => {
  const pattern = primaryBoilerDevice.value?.item?.pattern;
  if (pattern === undefined || pattern === null) {
    return undefined;
  }
  const parsed = parseInt(pattern, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
});

const isPatternMode = computed(() => (boilerPattern.value ?? 0) > 0);

/** 패턴 OFF 구간에서 현재온도 > 희망온도 → 목표 도달 난방정지로 추론 */
const isTargetReached = computed(() => {
  const current = parseFloat(boilerCurrentTemp.value);
  const target = parseFloat(boilerTargetTemp.value);
  if (Number.isNaN(current) || Number.isNaN(target)) {
    return false;
  }
  return current > target;
});

const hcMode = computed(() => {
  return primaryBoilerDevice.value?.item?.hcMode === 1;
});

/**
 * 에어컨(hcMode): 보드가 운전 중에도 POWER1=OFF 를 유지하고
 * AWAYMODESET(0=운전/1=정지) 으로만 알려 줌 → onoff/isAway 기준으로 표시.
 * 보일러: 기존처럼 릴레이(relayOn) 기준.
 */
const isAirconRunning = computed(() => hcMode.value && !isAway.value);

const boilerHeatPhase = computed(() => {
  if (hcMode.value) {
    return isAirconRunning.value ? 'running' : 'stopped';
  }
  if (isAway.value) {
    return 'stopped';
  }

  const relayIsOn = relayOn.value === 1;

  if (boilerPattern.value === undefined) {
    return relayIsOn ? 'heating' : 'stopped';
  }

  if (isPatternMode.value) {
    if (relayIsOn) {
      return 'patternHeating';
    }
    return isTargetReached.value ? 'stopped' : 'patternPause';
  }

  return relayIsOn ? 'heating' : 'stopped';
});

const tempModelLabel = computed(() => {
  const model = resolveTempControllerModel(
    primaryBoilerDevice.value,
    zone.value?.controllers,
  );
  if (!/^[A-Za-z]\d{2}$/.test(model)) {
    return '';
  }
  return model.toUpperCase();
});

const deviceLabel = computed(() => {
  const base = hcMode.value ? '에어컨' : '보일러';
  return tempModelLabel.value ? `${base} (${tempModelLabel.value})` : base;
});

const AWAY_BLUE = {
  text: '#1976d2',
};

const statusText = computed(() => {
  if (isControllerChecking.value) {
    return '조회중';
  }
  if (isControllerOffline.value) {
    return '오프라인';
  }
  if (hcMode.value) {
    return isAirconRunning.value ? currentTempText.value : '정지';
  }
  if (isAway.value) {
    return '외출중';
  }
  switch (boilerHeatPhase.value) {
    case 'patternHeating':
      return `(패턴${boilerPattern.value})난방중`;
    case 'patternPause':
      return `(패턴${boilerPattern.value})난방멈춤`;
    case 'heating':
      return '난방중';
    default:
      return '난방정지';
  }
});

const statusTextStyle = computed(() => {
  if (isControllerOffline.value) {
    return { color: '#dc3545' };
  }
  if (!hcMode.value && isAway.value) {
    return { color: AWAY_BLUE.text };
  }
  return {};
});

const cardTheme = computed(() => {
  if (isControllerOffline.value || isControllerChecking.value) {
    return BOILER_CARD.offline;
  }
  if (hcMode.value) {
    return isAirconRunning.value
      ? { bg: '#90caf9', border: '#64b5f6' }
      : BOILER_CARD.stopped;
  }
  return BOILER_CARD[boilerHeatPhase.value] || BOILER_CARD.stopped;
});

const cardActiveBg = computed(() => cardTheme.value.bg);

const cardDividerBorder = computed(() => cardTheme.value.border);

const powerActionLabel = computed(() => {
  if (hcMode.value) {
    return isAirconRunning.value ? '정지' : '운전';
  }
  return isAway.value ? '난방' : '외출';
});

const powerBtnStyle = computed(() => {
  const base = {
    borderRadius: '10px',
  };
  if (isControllerOffline.value || isControllerChecking.value) {
    const theme = BOILER_CARD.offline;
    return {
      ...base,
      border: `1px solid ${theme.btnBorder}`,
      backgroundColor: theme.btnBg,
      color: '#6c757d',
    };
  }
  if (hcMode.value) {
    if (!isAirconRunning.value) {
      return {
        ...base,
        border: '1px solid #ced4da',
        backgroundColor: '#f8f9fa',
      };
    }
    return {
      ...base,
      border: '1px solid #42a5f5',
      backgroundColor: '#64b5f6',
    };
  }
  if (isAway.value) {
    return {
      ...base,
      border: '1px solid #ced4da',
      backgroundColor: '#f8f9fa',
    };
  }
  if (boilerHeatPhase.value === 'patternPause') {
    const theme = BOILER_CARD.patternPause;
    return {
      ...base,
      border: `1px solid ${theme.btnBorder}`,
      backgroundColor: theme.btnBg,
    };
  }
  if (boilerHeatPhase.value === 'stopped') {
    return {
      ...base,
      border: '1px solid #ced4da',
      backgroundColor: '#f8f9fa',
    };
  }
  return {
    ...base,
    border: '1px solid #ffc082',
    backgroundColor: '#ffd082',
  };
});

const clearCheckTimer = () => {
  if (checkTimerId.value) {
    clearTimeout(checkTimerId.value);
    checkTimerId.value = undefined;
  }
};

const probeControllerOnline = async () => {
  const serial = mqttControllerId.value;
  if (!serial) {
    connectionStatus.value = 0;
    return;
  }

  clearCheckTimer();
  connectionStatus.value = 1;
  store.dispatch('home/updateCheckControllers', { serial, value: 0 });

  const deviceId = primaryBoilerDevice.value?._id;
  if (deviceId && schedules.value.length === 0) {
    try {
      const result = await Axios.get(ScheduleApi.SCHEDULES, { deviceId });
      if (result.resultCode === '0000') {
        schedules.value = result.resultData || [];
      }
    } catch (error) {
      console.log(error);
    }
  }

  // 컨트롤러가 바뀌었으면 중단
  if (mqttControllerId.value !== serial) {
    return;
  }

  const value = schedules.value?.length > 0 ? (schedules.value[0].isOn ? '1' : '0') : '0';
  mqttControl.onPublishTemp(serial, MQTTTempCommand.TimerGroupEnable1, value);

  checkTimerId.value = setTimeout(() => {
    if (connectionStatus.value === 1 && mqttControllerId.value === serial) {
      connectionStatus.value = 3;
      store.dispatch('home/updateCheckControllers', { serial, value: 3 });
    }
  }, 3000);
};

watch(
  () => (mqttControllerId.value ? checkControllers.value[mqttControllerId.value] : 0),
  (newValue) => {
    if (newValue === 1 && (connectionStatus.value === 1 || connectionStatus.value === 3)) {
      connectionStatus.value = 2;
      clearCheckTimer();
    }
  },
);

watch(
  mqttControllerId,
  (id) => {
    schedules.value = [];
    if (id) {
      probeControllerOnline();
    } else {
      clearCheckTimer();
      connectionStatus.value = 0;
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  clearCheckTimer();
});

const onBoilerPowerClicked = (e) => {
  handlePower(() => {
    emit('onPowerClicked', {
      id: props.roomId,
      type: 2,
      onoff: isAway.value ? PowerState.On : PowerState.Off,
    });
  });
};

const onBoilerInfoClicked = (e) => {
  boilerItemModal.show();
};

const onSettingClicked = (e) => {
  onBoilerInfoClicked();
};

const tempChanged = (e) => {
};

const onTouchDown = (e) => {
  if (!isAway.value) {
    handleTouchDown(e);
  }
};

const onTouchDrag = (e) => {
  handleTouchDrag(e, (screenX, startX) => {
  });
};

const onTouchRelease = (e) => {
  handleTouchRelease(e, tempChanged);
};

const onTouchHold = (e) => {
  handleTouchHold(e, onBoilerInfoClicked);
};
</script>

<style scoped>
.card-item {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.card-item:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.power-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(4rem - 8px);
  height: calc(4rem - 8px);
  min-width: calc(4rem - 8px);
  font-size: 0.8rem;
  font-weight: normal;
  flex-shrink: 0;
  cursor: pointer;
  user-select: none;
}
</style>
